<script>
import useSharedSelectData from "@/use/SharedSelectData.js";
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import {computed, onMounted, ref} from "vue";
import useTools from "@/use/Tools.js";
import useSharedMethod from "@/use/SharedMethod.js";
import {useBalanceStore} from "@/stores/Balance.js";
import usePeriodicity from "@/use/Periodicity.js";
import axios from "axios";

export default {
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
    },
    setup(props) {
        const {mxRules} = useValidation();
        const {dialog, loading, overlay, inputColor, backgroundColor} = useSharedData();

        const {periodicity, months, days, monthNumberDays, halfYearlyMonths} = usePeriodicity();

        const {balancePaySlipIncreaseOptions, operations} = useSharedSelectData();

        const {$refs, $route, $i18n} = useTools();

        const {getBack, isTwoTest} = useSharedMethod();

        const readonly = computed(() => props.viewAction || props.deleteAction);

        const balanceStore = useBalanceStore();

        const balance = ref({
            consolidationType: "SUM",
            allowNegative: false,
            allowManualProduce: false,
            allowManualConsume: false,
            allowAutoProduce: false,
            activateConsolidation: false,
            conditions: []
        });

        function findById(id) {
            loading.value = true;
            axios.get(`/balances/${id}`).then((response) => {
                balance.value = response.data;
                balance.value.conditions = balance.value.conditions.sort((a, b) => {
                    return a.order - b.order;
                });
            }).finally(() => {
                loading.value = false;
            });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/balances', balance.value).then((response) => {
                    balanceStore.addBalance(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/balances', balance.value).then((response) => {
                    balanceStore.updateBalance(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            loading.value = true;
            axios.delete(`/balances/${balance.value.id}`).then(() => {
                balanceStore.deleteBalance(balance.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }

        function getConditionTitle(type) {
            switch (type) {
                case 'ENTER_DATE':
                    return $i18n.t('worker.recruitment_date');
                case 'LEAVING_DATE':
                    return $i18n.t('common.exit_date');
            }
        }

        function addCondition(type) {
            balance.value.conditions.push({
                type: type,
                byAbsenceNumberElseDateEnter: true,
                order: balance.value.conditions.length + 1,
            });
        }

        function removeCondition(condition) {
            let index = balance.value.conditions.findIndex(c => c.type === condition.type && c.order === condition.order);
            balance.value.conditions.splice(index, 1);
        }

        onMounted(() => {
            if (!props.addAction)
                findById($route.params.id);
        });

        return {
            readonly,
            mxRules,
            balancePaySlipIncreaseOptions,
            dialog,
            loading,
            overlay,
            inputColor,
            backgroundColor,
            balance,
            periodicity,
            months,
            days,
            monthNumberDays,
            halfYearlyMonths,
            operations,
            getBack,
            isTwoTest,
            save,
            update,
            remove,
            getConditionTitle,
            addCondition,
            removeCondition,
        }
    },
}
</script>

<template>
    <v-dialog
        transition="dialog-bottom-transition"
        persistent
        v-model="dialog"
        max-width="950"
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :view-action="viewAction"
            ></w-dialog-toolbar>
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            ></w-overlay>
            <v-card-text class="ma-0 pa-0">
                <v-form ref="form" lazy-validation>
                    <v-tabs
                        height="750"
                         class="tabs"
                        color="teal"
                        :background-color="backgroundColor"
                        vertical
                        slider-size="7"
                    >
                        <v-tab>
                            <v-icon left>mdi-cog</v-icon>
                            {{ $t('common.params') }}
                        </v-tab>
                        <v-tab>
                            <v-icon left>mdi-ungroup</v-icon>
                            {{ $t('balance.consolidation') }}
                        </v-tab>
                        <v-tab>
                            <v-icon left>mdi-alert-box</v-icon>
                            {{ $t('common.conditions') }}
                        </v-tab>

                        <v-tab-item eager>
                            <v-container fluid>
                                <w-fieldset :title="$t('common.details')">
                                    <v-row dense>
                                        <v-col cols="12">
                                            <w-label :label="$t('common.name')" required/>
                                            <w-text-field
                                                v-model="balance.name"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="12">
                                            <w-label :label="$t('balance.init_value')" required/>
                                            <w-text-field
                                                v-model="balance.defaultInitValue"
                                                type="number"
                                                min="0"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="12">
                                            <w-label :label="$t('balance.unity')" required/>
                                            <v-radio-group
                                                v-model="balance.unite"
                                                row
                                                class="mt-n1"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                                hide-details="auto"
                                            >
                                                <v-radio value="HOURS" :label="$t('balance.HOURS')"/>
                                                <v-radio value="DAYS" :label="$t('balance.DAYS')"/>
                                            </v-radio-group>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-checkbox
                                                v-model="balance.allowNegative"
                                                :readonly="readonly"
                                                :label="$t('balance.allow_negative')"
                                            />
                                        </v-col>
                                    </v-row>
                                </w-fieldset>
                                <br/>
                                <w-fieldset
                                    :title="`${$t('balance.produce')} / ${$t('balance.consume')}`"
                                >
                                    <v-row dense>
                                        <v-col cols="6">
                                            <v-checkbox
                                                v-model="balance.allowManualProduce"
                                                :label="$t('balance.allow_manual_produce')"
                                                color="teal"
                                                :readonly="readonly"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <v-checkbox
                                                v-model="balance.allowManualConsume"
                                                :label="$t('balance.allow_manual_consume')"
                                                color="red"
                                                :readonly="readonly"
                                                hide-details
                                            />
                                        </v-col>
                                        <v-col cols="12">
                                            <v-checkbox
                                                v-model="balance.allowAutoProduce"
                                                :label="$t('balance.allow_auto_produce')"
                                                :readonly="readonly"
                                                hide-details
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row dense v-if="balance.allowAutoProduce">
                                        <v-col cols="12">
                                            <w-label :label="$t('common.value')" required/>
                                            <w-text-field
                                                v-model="balance.defaultIncreaseValue"
                                                type="number"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-label :label="$t('balance.mode')" required/>
                                            <w-enum-select
                                                v-model="balance.increaseWay"
                                                :items="[
                                                    {id: 'DATE', name: $t('balance.increase_way_DATE')},
                                                    {id: 'PAY_SLIP', name: $t('balance.increase_way_PAY_SLIP')},
                                                ]"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="6" v-if="balance.increaseWay === 'DATE'">
                                            <w-label :label="$t('common.periodicity')" required/>
                                            <w-enum-select
                                                v-model="balance.periodicity"
                                                :items="periodicity"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                    </v-row>
                                    <v-row
                                        v-if="balance.increaseWay === 'DATE' && balance.allowAutoProduce"
                                        dense
                                    >
                                        <v-col
                                            cols="12"
                                            v-if="balance.periodicity === 'daily'"
                                        >
                                            <w-label :label="$t('common.days')" required/>
                                            <w-enum-select
                                                v-model="balance.daysOfWeek"
                                                :items="days"
                                                multiple
                                                :readonly="readonly"
                                                :rules="[mxRules.listRequired]"
                                            />
                                        </v-col>
                                        <v-col
                                            v-if="balance.periodicity === 'weekly'"
                                            cols="12"
                                        >
                                            <w-label :label="$t('common.day')" required/>
                                            <w-enum-select
                                                v-model="balance.dayOfWeek"
                                                :items="days"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col
                                            v-if="balance.periodicity === 'halfYearly'"
                                            cols="12"
                                        >
                                            <w-label :label="$t('common.month_half_year')" required/>
                                            <w-enum-select
                                                v-model="balance.monthOfHalfYear"
                                                :items="halfYearlyMonths"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col
                                            v-if="balance.periodicity === 'yearly'"
                                            cols="12"
                                        >
                                            <w-label :label="$t('common.month')" required/>
                                            <w-enum-select
                                                v-model="balance.month"
                                                :items="months"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col
                                            cols="12"
                                            v-if="['monthly', 'halfYearly', 'yearly'].includes(balance.periodicity)"
                                        >
                                            <w-label :label="$t('common.day_of_month')" required/>
                                            <w-enum-select
                                                v-model="balance.dayOfMonth"
                                                :items="monthNumberDays"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                    </v-row>
                                </w-fieldset>
                            </v-container>
                        </v-tab-item>
                        <v-tab-item eager>
                            <v-container fluid>
                                <v-row>
                                    <v-col cols="12">
                                        <v-checkbox
                                            v-model="balance.activateConsolidation"
                                            :label="$t('balance.allow_consolidation')"
                                            :readonly="readonly"
                                        />
                                    </v-col>
                                </v-row>
                                <w-fieldset
                                    v-if="balance.activateConsolidation"
                                    :title="$t('balance.consolidation_period')"
                                >

                                    <v-row>
                                        <v-col cols="6">
                                            <w-label :label="$t('common.from')" required/>
                                            <w-enum-select
                                                v-model="balance.consolidationStartMonth"
                                                :items="months"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-label :label="$t('common.to')" required/>
                                            <w-enum-select
                                                v-model="balance.consolidationEndMonth"
                                                :items="months"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                    </v-row>
                                </w-fieldset>
                                <br/>
                                <w-fieldset
                                    v-if="balance.activateConsolidation"
                                    :title="$t('balance.consolidation_date')"
                                >
                                    <v-row dense>
                                        <v-col cols="6">
                                            <w-label :label="$t('common.day')" required/>
                                            <w-enum-select
                                                v-model="balance.consolidationRunningDay"
                                                :items="monthNumberDays"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                        <v-col cols="6">
                                            <w-label :label="$t('common.month')" required/>
                                            <w-enum-select
                                                v-model="balance.consolidationRunningMonth"
                                                :items="months"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                    </v-row>
                                </w-fieldset>
                                <br/>
                                <w-fieldset
                                    v-if="balance.activateConsolidation"
                                    :title="$t('balance.remaining')"
                                >
                                    <v-row dense>
                                        <v-col cols="12">
                                            <v-checkbox
                                                v-model="balance.consolidationType"
                                                true-value="REMAINING"
                                                false-value="SUM"
                                                :label="$t('balance.allow_remaining')"
                                                :readonly="readonly"
                                                hide-details="auto"
                                            />
                                        </v-col>
                                        <v-col
                                            v-if="balance.consolidationType === 'REMAINING'"
                                            cols="12"
                                        >
                                            <w-label :label="$t('balance.remaining_number')" required/>
                                            <w-text-field
                                                v-model="balance.remainingNumber"
                                                type="number"
                                                :readonly="readonly"
                                                :rules="[mxRules.required]"
                                            />
                                        </v-col>
                                    </v-row>
                                    <br/>
                                    <v-simple-table
                                        v-if="balance.consolidationType === 'REMAINING' && balance.remainingNumber"
                                        dense
                                        fixed-header
                                        height="200"
                                        class="elevation-5"
                                    >
                                        <template v-slot:default>
                                            <thead>
                                            <tr>
                                                <th class="text-left">{{ $t('common.name') }}</th>
                                                <th class="text-left">{{ $t('balance.remaining_action') }}</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            <tr
                                                v-for="index in parseInt(balance.remainingNumber)"
                                                :key="`remaining-${index}`"
                                            >
                                                <td>{{ `${$t('balance.totalRemaining')} N-${index}` }}</td>
                                                <td v-if="index === parseInt(balance.remainingNumber)">
                                                    {{ $t('balance.remaining_sum') }}
                                                </td>
                                                <td v-else>{{ $t('balance.remaining_transfer') }}</td>
                                            </tr>
                                            </tbody>
                                        </template>
                                    </v-simple-table>
                                </w-fieldset>
                            </v-container>
                        </v-tab-item>
                        <v-tab-item eager>
                            <v-container fluid>
                                <w-fieldset
                                    v-for="(type, index) in   ['ENTER_DATE', 'LEAVING_DATE']"
                                    :key="`type-${index}`"
                                    :title="getConditionTitle(type)"
                                    show-add-action
                                    @add="addCondition(type)"
                                >
                                    <v-container fluid>
                                        <v-alert
                                            text
                                            dense
                                            color="teal"
                                            icon="mdi-clock-fast"
                                            border="left"
                                        >
                                            {{ $t(`balance.${type}_condition_description`) }}
                                        </v-alert>
                                        <v-row
                                            v-for="(condition, index) in balance.conditions.filter(c => c.type === type)"
                                            :key="index"
                                            dense
                                        >
                                            <v-col cols="3">
                                                <w-label :label="$t('common.operation')" required/>
                                                <w-enum-select
                                                    v-model="condition.operator"
                                                    :items="operations"
                                                    :readonly="readonly"
                                                    :rules="[mxRules.required]"
                                                />
                                            </v-col>
                                            <v-col cols="3">
                                                <w-label :label="$t('common.first_test')" required/>
                                                <w-text-field
                                                    v-model="condition.firstTest"
                                                    type="number"
                                                    :readonly="readonly"
                                                    :rules="[mxRules.required]"
                                                />
                                            </v-col>
                                            <v-col cols="3">
                                                <w-label :label="$t('common.second_test')" :required="isTwoTest(condition.operator)"/>
                                                <w-text-field
                                                    v-model="condition.secondTest"
                                                    :disabled="!isTwoTest(condition.operator)"
                                                    type="number"
                                                    :readonly="readonly"
                                                    :rules="isTwoTest(condition.operator) ? [mxRules.required] : []"
                                                />
                                            </v-col>
                                            <v-col cols="2">
                                                <w-label :label="$t('common.result')" required/>
                                                <w-text-field
                                                    v-model="condition.result"
                                                    type="number"
                                                    :readonly="readonly"
                                                    :rules="[mxRules.required]"
                                                ></w-text-field>
                                            </v-col>
                                            <v-col cols="1">
                                                <w-btn-icon
                                                    :disabled="readonly"
                                                    icon="mdi-trash-can"
                                                    color="red"
                                                    classe="mt-5"
                                                    @press="removeCondition(condition)"
                                                />
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </w-fieldset>
                            </v-container>

                        </v-tab-item>
                    </v-tabs>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped>
div.tabs [role="tab"] {
    justify-content: flex-start;
}
</style>